import axios from 'axios'
import { toCamelCase, toUnderscore, buildQueryString } from './utils'

const xsrfTag = document.querySelector('meta[name=csrf-token]')
const xsrfToken = xsrfTag && xsrfTag.getAttribute('content') // from rails

const bmAxios = axios.create({
  timeout: 25000,
  headers: {
    'X-XSRF-TOKEN': xsrfToken,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  responseType: 'json',
  paramsSerializer: (params) => buildQueryString(toUnderscore(params))
})

let sequential_fail_url_counters = {}
const RETRIES_BEFORE_REPORT: number = 1

bmAxios.interceptors.response.use(response => {
  sequential_fail_url_counters[response.config.url] = 0
  return toCamelCase(response.data)
}, error => {
  // any special error handling here
  if (!error.response) {
    const c = error.config
    if (c.url !== '/api/errors') {
      if (sequential_fail_url_counters[c.url] && sequential_fail_url_counters[c.url] >= RETRIES_BEFORE_REPORT) {
        sequential_fail_url_counters[c.url] = 0
        error.message += `
        \nMethod: ${c.method}
        \nUrl: ${c.url}
        \nParams: ${JSON.stringify(c.params)}
        \nData: ${JSON.stringify(c.data)}
        \nTimeout: ${c.timeout}
        \nHeaders: ${JSON.stringify(c.headers)}`
        throw new Error(error)
      } else {
        sequential_fail_url_counters[c.url] = sequential_fail_url_counters[c.url] ? sequential_fail_url_counters[c.url] + 1 : 1

        throw new Error("IGNORE")
      }
    }
  }

  return Promise.reject({
    status: error.response ? error.response.status : null,
    statusText: error.response ? error.response.statusText : null,
    body: error.response ? toCamelCase(error.response.data) : null
  })
})

type Params = { [key: string]: any };
type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

const base = (method: Method, url: string, config: object = {}): Promise<any> => {
  const conf = Object.assign(config, { method, url})
  if (config['body']) {
    const b = config['body']
    conf['data'] = b.constructor === Object ? toUnderscore(b) : b
  }
  return bmAxios.request(conf)
}

const get = (url: string, params: Params = null) => {
  return base('get', url, { params })
}

const post = (url: string, body: object | string = {}, config: object = {}) => {
  return base('post', url, Object.assign(config, { body }))
}

const put = (url: string, body: object | string = {}, config: object = {}) => {
  return base('put', url, Object.assign(config, { body }))
}

const patch = (url: string, body: object | string = {}, config: object = {}) => {
  return base('patch', url, Object.assign(config, { body }))
}

const del = (url: string) => {
  return base('delete', url)
}

const request = { get, post, put, patch, del }

export { request, xsrfToken }
export default request
